import React, { useContext, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiX } from "react-icons/bi";
import { BsFilter } from "react-icons/bs";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import { RegulationContext } from "../../../../App";
import { CategoryList, CompareCategoryType, DataViewCategoryType, LS_COMP_FILTER_KEY, MonthsExt, StandardCategory, startYear, LS_REGULATION_SELECTED_KEY } from "../../../../core/constants/common";
import CompanyService from "../../../../core/service/CompanyService";
import StandardService from "../../../../core/service/StandardService";
import Utils from "../../../../core/utilities/Utils";
import {
	getCompanyInfoFromStorage,
	getMaxSelectableYear,
	getMaxYearlyPeriodEndYear,
	getYearDropdownLabel,
	isFinancialYearReporting,
} from "../../../../core/utilities/assessmentPeriodUtils";
import './CompareFilter.scss';

// Same react-select building blocks/skin ("dvSelect") as DataViewFilterNew.js,
// kept local here the same way DataViewFilterNew.js keeps them local.
const MAX_VISIBLE_CHIPS = 2;

const flattenSelectOptions = (options) => {
	return (options ?? []).reduce((acc, item) => {
		if (item?.options) {
			return acc.concat(flattenSelectOptions(item.options));
		}
		acc.push(item);
		return acc;
	}, []);
};

const MultiSelectOption = (props) => {
	const { isSelected, label } = props;
	return (
		<components.Option {...props}>
			<Form.Check
				type="checkbox"
				readOnly
				checked={isSelected}
				label={label}
				className="dvSelect-option-check" />
		</components.Option>
	);
};

const MultiValueChip = (props) => {
	const { index, getValue } = props;
	if (index < MAX_VISIBLE_CHIPS) {
		return <components.MultiValue {...props} />;
	}
	if (index === MAX_VISIBLE_CHIPS) {
		return (
			<div className="dvSelect__multi-value dvSelect__multi-value--overflow">
				<span className="dvSelect__multi-value__label">{props.selectProps.moreLabel(getValue().length - MAX_VISIBLE_CHIPS)}</span>
			</div>
		);
	}
	return null;
};

const MultiSelectMenuList = (props) => {
	const { children, options, selectProps } = props;
	return (
		<components.MenuList {...props}>
			<div className="dvSelect__menu-actions">
				<button type="button"
					className="dvSelect__menu-action"
					onMouseDown={(e) => {
						e.preventDefault();
						const flatOptions = flattenSelectOptions(options);
						const limitedOptions = selectProps.selectionLimit
							? flatOptions.slice(0, selectProps.selectionLimit)
							: flatOptions;
						selectProps.onChange(limitedOptions, { action: 'select-option' });
					}}>
					{selectProps.selectAllLabel}
				</button>
				<button type="button"
					className="dvSelect__menu-action dvSelect__menu-action--clear"
					onMouseDown={(e) => { e.preventDefault(); selectProps.onChange([], { action: 'clear' }); }}>
					{selectProps.clearAllLabel}
				</button>
			</div>
			{children}
		</components.MenuList>
	);
};

const CompareFilter = ({ t, onApplyFilter, onResetFilter, onFilterHide }) => {

	const compFilter = JSON.parse(localStorage.getItem(LS_COMP_FILTER_KEY));
	const { glbRegulation } = useContext(RegulationContext);
	const glbRegulationId = localStorage.getItem(LS_REGULATION_SELECTED_KEY);
	const month = Utils.getMonthFromFrequency(glbRegulation.frequency);
	const year = new Date().getFullYear();

	const [filter, setFilter] = useState({
		standardId: compFilter?.glbRegulationId ?? glbRegulationId,
		categoryType: compFilter?.categoryType ?? DataViewCategoryType.ENVIRONMENTAL,
		topicIds: compFilter?.topicIds ?? [],
		metricIds: compFilter?.metricIds ?? [],
		businessUnitIds: compFilter?.businessUnitIds ?? [],
		parentEntities: compFilter?.parentEntities ?? [],
		childEntities: compFilter?.childEntities ?? [],
		periodType: compFilter?.periodType ?? 'YEARLY',
		period: compFilter?.period ?? null,
		selectedRepPeriod: compFilter?.selectedRepPeriod ?? [],
	});
	const [topics, setTopics] = useState([]);
	const [parentEntities, setParentEntities] = useState([]);
	const [childEntities, setChildEntities] = useState([]);

	const [period, setPeriod] = useState([]);
	const [displayFilter, setDisplayFilter] = useState(true);
	const optionsMonth = MonthsExt({ t });
	const currentYear = new Date().getFullYear();
	const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
	const [selectedData, setSelectedData] = useState(compFilter?.selectedRepPeriod ?? []);

	useEffect(() => {
		if (!glbRegulationId) return;
		getTopics();
		getEnterprisesByLevel(1);
		getEnterprisesByLevel(2);
	}, [glbRegulationId, filter.categoryType]);

	useEffect(() => {
		setPeriodItems('onload', filter.periodType);
	}, [filter.periodType]);

	useEffect(() => {
		setFilter({ ...filter, selectedRepPeriod: selectedData });
	}, [selectedData]);

	const setPeriodItems = (type, value) => {
		const yearOptions = [];
		if (value === 'YEARLY') {
			const companyInfo = getCompanyInfoFromStorage();
			const isFinancialYear = isFinancialYearReporting(companyInfo);
			const maxEndYear = isFinancialYear
				? getMaxYearlyPeriodEndYear(companyInfo)
				: getMaxSelectableYear();
			let count = 0;
			for (let i = maxEndYear; i >= startYear; i--) {
				yearOptions.push({
					id: count,
					value: i,
					displayValue: getYearDropdownLabel(i, isFinancialYear),
					category: 'Years'
				});
				count++
			}
			setPeriod(yearOptions);
			if (type !== 'direct') {
				setSelectedData((prev) => {
					if (!prev?.length) return prev;
					return prev.map((item) => ({
						...item,
						displayValue: getYearDropdownLabel(item.value, isFinancialYear),
					}));
				});
			}
		} else if (value === 'QUARTERLY') {
			let count = 0;
			for (let i = currentYear; i >= startYear; i--) {
				for (let j = 0; j < quarters.length; j++) {
					yearOptions.push({
						category: i,
						value: quarters[j],
						displayValue: i + ' - ' + quarters[j],
						id: count,
					});
					count++
				}
			}
			// yearOptions.sort((a, b) => b.category - a.category);
			setPeriod(yearOptions);
		} else if (value === 'MONTHLY') {
			let count = 0;
			for (let i = currentYear; i >= startYear; i--) {
				for (let j = 0; j < optionsMonth.length; j++) {
					yearOptions.push({
						category: i,
						value: optionsMonth[j].value,
						displayValue: i + ' - ' + optionsMonth[j].label,
						id: count,
					});
					count++
				}
			}
			// yearOptions.sort((a, b) => b.category - a.category);
			setPeriod(yearOptions);
		} else {
			setPeriod([]);
			// setSelectedData([])
		}
		if (type == 'direct') {
			setSelectedData([]);
		}
	}

	// Groups quarter/month period options under their year so react-select can
	// render them as option groups (same grouping shape DataViewFilterNew.js
	// uses for child entities). Yearly periods share one category, so they stay flat.
	const getGroupedPeriodOptions = () => {
		if (filter.periodType === 'YEARLY' || !period?.length) return period;

		const groupsByLabel = new Map();
		const labelOrder = [];
		period.forEach(item => {
			const label = String(item.category);
			if (!groupsByLabel.has(label)) {
				groupsByLabel.set(label, []);
				labelOrder.push(label);
			}
			groupsByLabel.get(label).push(item);
		});
		return labelOrder.map(label => ({ label, options: groupsByLabel.get(label) }));
	};

	const onPeriodChange = (selectedItems) => {
		const newData = selectedItems ?? [];
		setSelectedData(newData);
		preparePeriod(newData);
	};

	const preparePeriod = (data) => {
		if (filter.periodType == 'YEARLY') {
			const objectTags = Object.fromEntries(data.map((t) => [t.value, []]))
			setFilter({
				...filter,
				period: objectTags,
			});
		} else if (filter.periodType == 'QUARTERLY') {
			const result = Object.groupBy(data, ({ category }) => category);
			for (const year in result) {
				let values = data.filter(element => element['category'] == year);
				result[year] = [];
				values.forEach(data => {
					result[year].push(data['value']);
				})
			}
			setFilter({
				...filter,
				period: result,
			});
		} else {
			const result = Object.groupBy(data, ({ category }) => category);
			for (const year in result) {
				let values = data.filter(element => element['category'] == year);
				result[year] = [];
				values.forEach(data => {
					result[year].push(data['value']);
				})
			}
			setFilter({
				...filter,
				period: result,
			});
		}
	}

	const getTopics = () => {
		StandardService.getTopicsByStandard(glbRegulationId, false, StandardCategory.QUANTITATIVE, filter.categoryType)
			.then(res => {
				if (res.status !== 200) return;
				setTopics(res.data);
			}).catch(err => { });
	};

	const getEnterprisesByLevel = (level) => {
		CompanyService.getEnterprisesByLevel(level).then(res => {
			if (res.status !== 200) return;

			const entities = res.data.sort((a, b) => a.sortOrder - b.sortOrder);
			if (level === 1) {
				setParentEntities(entities);
			} else if (level === 2) {
				setChildEntities(entities);
			}
		}).catch(err => { });
	};

	const onTopicsChange = (selectedItems) => {
		setFilter({
			...filter,
			topicIds: (selectedItems ?? []).map(item => item.id),
			metricIds: [],
		});
	};

	const getSelectedTopics = () => {
		const data = [];
		topics?.forEach(topic => {
			if (filter.topicIds.indexOf(topic.id) < 0) return;

			data.push(topic);
		});
		return data;
	};

	const onParentEntitiesChange = (selectedItems) => {
		setFilter({
			...filter,
			parentEntities: (selectedItems ?? []).map(item => item.id)
		});
	};

	const onChildEntitiesChange = (selectedItems) => {
		setFilter({
			...filter,
			childEntities: (selectedItems ?? []).map(item => item.id)
		});
	};

	const getSelectedParentEntities = () => {
		const data = [];
		parentEntities?.forEach(p => {
			if (filter.parentEntities.indexOf(p.id) < 0) return;

			data.push(p);
		});
		return data;
	};

	const getSelectedChildEntities = () => {
		const data = [];
		childEntities?.forEach(c => {
			if (filter.childEntities.indexOf(c.id) < 0) return;

			data.push(c);
		});
		return data;
	};

	// Groups child entities under their parent business unit as a section heading
	// (same grouping DataViewFilterNew.js's getChildEntityOptions uses) so only the
	// child entities themselves are selectable, never the parent heading.
	const getChildEntityOptions = () => {
		if (!childEntities?.length || !parentEntities?.length) return childEntities;

		const groupsByLabel = new Map();
		const labelOrder = [];
		childEntities.forEach(child => {
			const parentId = child.parentId ?? child.parentBusinessUnitId ?? child.parent?.id ?? null;
			const parent = parentId != null ? parentEntities.find(p => p.id === parentId) : null;
			const groupLabel = parent?.name ?? t("Others.label");
			if (!groupsByLabel.has(groupLabel)) {
				groupsByLabel.set(groupLabel, []);
				labelOrder.push(groupLabel);
			}
			groupsByLabel.get(groupLabel).push(child);
		});

		if (labelOrder.length <= 1) return childEntities;
		return labelOrder.map(label => ({ label, options: groupsByLabel.get(label) }));
	};

	const applyFilter = () => {
		const filterData = JSON.parse(JSON.stringify(filter));
		filterData.businessUnitIds = [];
		filterData.parentEntities?.forEach(e => {
			filterData.businessUnitIds.push(e)
		});
		filterData.childEntities?.forEach(e => {
			filterData.businessUnitIds.push(e)
		});
		const compareFilter = {
			standardId: glbRegulationId,
			periodType: filterData.periodType,
			period: filterData.period,
			category: filterData.categoryType ? CompareCategoryType[filterData.categoryType] : 'ENVIRONMENTAL',
			topicIds: filterData.topicIds,
			parentBranchIds: filterData.parentEntities,
			childBranchIds: filterData.childEntities,
			businessUnitIds: filterData.businessUnitIds,
			childEntities: compFilter?.childEntities,
			parentEntities: compFilter?.parentEntities,
			selectedRepPeriod: compFilter?.selectedRepPeriod,
		}
		if (compareFilter.period != null) {
			onApplyFilter(compareFilter);
			localStorage.setItem(LS_COMP_FILTER_KEY, JSON.stringify(filterData));
			onFilterHide(false);
		} else {
			toast.warning('Select any period', {
				position: toast.POSITION.TOP_RIGHT,
				autoClose: 2000
			});
		}
	};

	const resetFilter = () => {
		setFilter({
			standardId: glbRegulationId,
			periodType: 'YEARLY',
			period: null,
			category: 'ENVIRONMENTAL',
			topicIds: [],
			parentBranchIds: [],
			childBranchIds: [],
			businessUnitIds: [],
			childEntities: [],
			parentEntities: [],
			selectedRepPeriod: []
		});
		localStorage.removeItem(LS_COMP_FILTER_KEY);
		setSelectedData([])
		onResetFilter();
	};

	const renderReportingPeriodFilter = () => {
		const periodSelectProps = {
			classNamePrefix: "dvSelect",
			unstyled: true,
			isMulti: true,
			placeholder: selectedData.length === 0 ? t("Select.label") : "",
			noOptionsMessage: () => t("NoData.label"),
			selectAllLabel: t("SelectAll.label"),
			clearAllLabel: t("ClearAll.label"),
			moreLabel: (count) => t("MoreSelected.label", { count }),
			components: { Option: MultiSelectOption, MultiValue: MultiValueChip, MenuList: MultiSelectMenuList },
			hideSelectedOptions: false,
			closeMenuOnSelect: false,
			// menuPlacement: "top",
			menuPosition: "fixed",
		};
		return (
			<div className="row m-0">
				<div className="col-lg-6 p-2 pb-1">
					<label className="mb-1">{t("Frequency.label")}</label>
					<div className="period_chooseBox">
						<Form.Check inline label={t("Yearly.label")}
							type="radio" id="freqYearRadioC" value={'YEARLY'}
							className="period_checkbox"
							checked={filter.periodType === 'YEARLY' ? "checked" : ""}
							onChange={(e) => {
								setFilter({ ...filter, periodType: e.target.value, period: null });
								setPeriodItems('direct', e.target.value);
							}} />
						<Form.Check inline label={t("Quarterly.label")}
							className="period_checkbox"
							type="radio" id="freqQuarterRadioC" value={'QUARTERLY'}
							checked={filter.periodType === 'QUARTERLY' ? "checked" : ""}
							onChange={(e) => {
								setFilter({ ...filter, periodType: e.target.value, period: null });
								setPeriodItems('direct', e.target.value);
							}} />
						<Form.Check inline label={t("Monthly.label")}
							className="period_checkbox"
							type="radio" id="freqMonthRadioC" value={'MONTHLY'}
							checked={filter.periodType === 'MONTHLY' ? "checked" : ""}
							onChange={(e) => {
								setFilter({ ...filter, periodType: e.target.value, period: null });
								setPeriodItems('direct', e.target.value);
							}} />
					</div>
				</div>
				<div className="col-lg-6 p-2 pb-1">
					<label htmlFor="periodMultiSelect" className="mb-1">{t("ReportingPeriod.label")}</label>
					<Select
						{...periodSelectProps}
						inputId="periodMultiSelect"
						options={getGroupedPeriodOptions()}
						value={selectedData}
						getOptionValue={(o) => o.id}
						getOptionLabel={(o) => o.displayValue}
						onChange={onPeriodChange}
						selectionLimit={6}
						isOptionDisabled={(o) => selectedData.length >= 6 && selectedData.findIndex(s => s.id === o.id) < 0} />
				</div>
			</div>
		);
	};

	const renderMetricRelatedFilters = () => {
		const commonSelectProps = {
			classNamePrefix: "dvSelect",
			unstyled: true,
			isMulti: true,
			placeholder: t("Select.label"),
			noOptionsMessage: () => t("NoData.label"),
			selectAllLabel: t("SelectAll.label"),
			clearAllLabel: t("ClearAll.label"),
			moreLabel: (count) => t("MoreSelected.label", { count }),
			components: { Option: MultiSelectOption, MultiValue: MultiValueChip, MenuList: MultiSelectMenuList },
			hideSelectedOptions: false,
			closeMenuOnSelect: false,
			// menuPlacement: "top",
			menuPosition: "fixed",
		};
		return (
			<div className="row m-0">
				<div className="col-lg-6 p-2 pb-1">
					<label htmlFor="esgFilter">{t("Category.label")}</label>
					<select id="esgFilter"
						className="form-select"
						onChange={(e) => setFilter({ ...filter, categoryType: +e.target.value, topicIds: [], metricIds: [] })}
						required
						value={filter.categoryType}>
						{
							CategoryList({ t }).map(c => {
								return <option value={c.id} key={c.key}>{c.text}</option>
							})
						}
					</select>
				</div>
				<div className="col-lg-6 p-2 pb-1">
					<label>{t("Topic.label")}</label>
					<Select
						{...commonSelectProps}
						inputId="topicMultiSelect"
						options={topics}
						value={getSelectedTopics()}
						getOptionValue={(o) => o.id}
						getOptionLabel={(o) => o.name}
						onChange={onTopicsChange}
						selectionLimit={5}
						isOptionDisabled={(o) => filter.topicIds.length >= 5 && filter.topicIds.indexOf(o.id) < 0} />
				</div>
				<div className="col-lg-6 p-2 pb-1">
					<label>{t("ParentEntity.label")}</label>
					<Select
						{...commonSelectProps}
						inputId="parentEntityMultiselect"
						options={parentEntities}
						value={getSelectedParentEntities()}
						getOptionValue={(o) => o.id}
						getOptionLabel={(o) => o.name}
						onChange={onParentEntitiesChange}
						selectionLimit={5}
						isOptionDisabled={(o) => filter.parentEntities.length >= 5 && filter.parentEntities.indexOf(o.id) < 0} />
				</div>
				<div className="col-lg-6 p-2 pb-1">
					<label>{t("ChildEntity.label")}</label>
					<Select
						{...commonSelectProps}
						inputId="childEntityMultiselect"
						options={getChildEntityOptions()}
						value={getSelectedChildEntities()}
						getOptionValue={(o) => o.id}
						getOptionLabel={(o) => o.name}
						onChange={onChildEntitiesChange}
						selectionLimit={5}
						isOptionDisabled={(o) => filter.childEntities.length >= 5 && filter.childEntities.indexOf(o.id) < 0} />
				</div>
				<div className="col-lg-6 p-2 pb-1"></div>
			</div>
		);
	};

	return (
		<div>
			{
				displayFilter &&
				<div className="dv-filter-holder 2">
					<div className="dv-filter-holder_head">
						<h5><BsFilter className="dv-filter-holder_headIcon" />{t("Filter.label")}</h5>
						<div className="btnClose" onClick={() => onFilterHide(false)}>
							<BiX />
						</div>
					</div>
					<div className="dv-filter-holder_body">
						<div className="dv-filter-section">
							{renderReportingPeriodFilter()}
						</div>
						<div className="dv-filter-section">
							{renderMetricRelatedFilters()}
						</div>
					</div>
					<div className="dv-filter-holder_footer">
						<Button variant="outline-secondary"
							onClick={() => resetFilter()}
							className="px-4">
							{t("Reset.label")}
						</Button>
						<Button variant="primary"
							onClick={() => applyFilter()}
							className="sbmBtn px-4">
							{t("Apply.label")}
						</Button>
					</div>
				</div>
			}
		</div>
	);
};

export default withTranslation()(CompareFilter);
